import React, { useState, useRef } from 'react';
import Modal from 'cccisd-modal';
import axios from 'cccisd-axios';
import Tooltip from 'cccisd-tooltip';
import { Formik, Form, Field, CccisdInput, CccisdSelect } from 'cccisd-formik';
import { updateSessionDeploymentInfo } from '../../helpers';
import MessageForm from '../Messages/Form';
import PlusIcon from 'cccisd-icons/plus-circle2';
import PencilIcon from 'cccisd-icons/pencil5';
import InfoIcon from 'cccisd-icons/info2';
import notification from 'cccisd-notification';
import validator from 'cccisd-validator';
import style from './style.css';

const Boilerplate = window.cccisd.boilerplate;
const isOrgProvider = window.cccisd.fortress.user.acting.role.handle === 'orgInstructor';
const contactPath = isOrgProvider
    ? 'parentRoles.orgLearner.childRoles.contactList'
    : 'parentRoles.learner.childRoles.contactList';

const FormModal = props => {
    const [contactInfo, setContactInfo] = useState({});
    const [assessmentLink, setAssessmentLink] = useState('');
    const [currentSessionDeploymentInfo, setCurrentSessionDeploymentInfo] = useState({});
    const modal = useRef();
    const messageRef = useRef();

    const isEdit = props.isEdit;
    let contactList = isEdit
        ? props.row[contactPath].filter(item => item.pawn.pawnId !== props.row['pawn.pawnId'])
        : props.row['childRoles.contactList'];

    const submit = async values => {
        if (isEdit) {
            // HAS CONTACT LIST

            const updatedPawnValues = {
                contactName: values.contactName,
                contactEmail: values.contactEmail,
                contactType: values.contactType,
            };
            updateContact(updatedPawnValues);
        }

        if (!isEdit) {
            if (!contactList || contactList.length === 0) {
                saveNewContact(values);
            } else {
                // has contactList
                // check if it exists

                const match = contactList.find(cont => cont.fields.contactEmail === values.contactEmail);
                if (match && !existsInCurrentPlan(values.contactEmail)) {
                    // update contact for this plan
                    const pawnId = match.pawn.pawnId;
                    const pawnHash = match.pawn.pawnHash;
                    const assignmentPlanIds = JSON.parse(match.fields.assignment_plan_ids);
                    assignmentPlanIds.push(props.assignmentPlanId);

                    const updateValues = {
                        contactName: values.contactName,
                        contactType: values.contactType,
                        contactEmail: values.contactEmail,
                        assignment_plan_ids: assignmentPlanIds,
                    };
                    updateContact(updateValues, pawnId, pawnHash);
                } else {
                    // they dont exist for this student create  them
                    saveNewContact(values);
                }
                // if exists in currentPlan validation will catch
            }
        }
    };

    const existsInCurrentPlan = email => {
        const match = contactList.find(cont => cont.fields.contactEmail === email);

        if (match) {
            // check planIds
            if (isEdit) {
                return true;
            }
            const ids = JSON.parse(match.fields.assignment_plan_ids);

            if (ids.includes(props.assignmentPlanId)) {
                return true;
            }
            return false;
        }
        return false;
    };

    const saveNewContact = async values => {
        // NO other person has this contact
        const newPawnValues = {
            role: 'contact',
            contactName: values.contactName,
            contactEmail: values.contactEmail,
            contactType: values.contactType,
            assignment_plan_ids: [props.assignmentPlanId],
            parentPawns: props.row['pawn.pawnId'],
        };

        try {
            const response = await axios.post(Boilerplate.route('api.nexus.pawn.store'), {
                ...newPawnValues,
            });

            if (response?.data?.status === 'success') {
                notification({ message: 'Contact added!', type: 'success' });
                modal.current.close();

                if (props.session) {
                    await updateSessionDeploymentInfo(
                        [
                            {
                                contactType: values.contactType,
                                email: values.contactEmail,
                                pawnId: response.data.data.id,
                                respondentHash: response.data.data.respondent_hash,
                            },
                        ],
                        props.session
                    ).then(res => {
                        const sessionDeploymentId =
                            res.find(sessionDeploymentInfo => sessionDeploymentInfo.pawnId === response.data.data.id)
                                ?.deploymentId || null;
                        setAssessmentLink(
                            Boilerplate.url(
                                `/sessionPlayer/${sessionDeploymentId}/${props.assignmentPlanId}/${
                                    props.session.sessionId
                                }/${values.contactType}/${response.data.data.id}/${
                                    response.data.data.random_hash
                                }?s=${props.session.settings.options.join(',')}`
                            )
                        );

                        setCurrentSessionDeploymentInfo({
                            pawnId: response.data.data.id,
                            deploymentId: sessionDeploymentId,
                        });
                    });

                    setContactInfo({ ...newPawnValues, ...response.data });
                    messageRef.current.open();
                }
            } else {
                notification({ message: 'An error occured adding contact', type: 'danger' });
            }
        } catch (e) {
            console.error(e);
            notification({ message: 'An error occured adding contact', type: 'danger' });
        }
    };

    const updateContact = async (updateValues, pawnId, hash) => {
        const pawn = isEdit ? props.row['pawn.pawnId'] : pawnId;
        const pawnHash = isEdit ? props.row['pawn.pawnHash'] : hash;

        try {
            const response = await axios.put(
                Boilerplate.route('api.nexus.pawn.update', {
                    pawn,
                }),
                {
                    pawnHash,
                    ...updateValues,
                }
            );

            if (response?.data?.status === 'success') {
                notification({ message: isEdit ? 'Contact updated!' : 'Contact added!', type: 'success' });
                modal.current.close();

                if (props.session && props.session.sessionId) {
                    await updateSessionDeploymentInfo(
                        [
                            {
                                contactType: updateValues.contactType,
                                email: updateValues.contactEmail,
                                pawnId: response.data.data.id,
                                respondentHash: response.data.data.respondent_hash,
                            },
                        ],
                        props.session
                    ).then(res => {
                        const sessionDeploymentId =
                            res.find(sessionDeploymentInfo => sessionDeploymentInfo.pawnId === response.data.data.id)
                                ?.deploymentId || null;
                        setAssessmentLink(
                            Boilerplate.url(
                                `/sessionPlayer/${sessionDeploymentId}/${props.assignmentPlanId}/${
                                    props.session.sessionId
                                }/${updateValues.contactType}/${response.data.data.id}/${
                                    response.data.data.random_hash
                                }?s=${props.session.settings.options.join(',')}`
                            )
                        );
                        setCurrentSessionDeploymentInfo({
                            pawnId: response.data.data.id,
                            deploymentId: sessionDeploymentId,
                        });
                    });

                    setContactInfo({ ...updateValues, ...response.data });
                    messageRef.current.open();
                }
            } else {
                notification({
                    message: isEdit ? 'An error occured updating contact' : 'An error occured adding contact',
                    type: 'danger',
                });
            }
        } catch (e) {
            console.error(e);
            notification({
                message: isEdit ? 'An error occured updating contact' : 'An error occured adding contact',
                type: 'danger',
            });
        }
    };

    const validate = values => {
        try {
            const errors = {};

            if (!values.contactName) {
                errors.contactName = 'Name is required';
            }

            if (!values.contactType) {
                errors.contactType = 'Type is required';
            }

            if (values.contactEmail) {
                const isEmail = validator(values.contactEmail, {
                    types: ['email'],
                    min: 1,
                    max: 255,
                    regex: '',
                });

                if (isEmail.status === 'pass') {
                    if (contactList && contactList.length > 0) {
                        const existsInPlan = existsInCurrentPlan(values.contactEmail);

                        if (existsInPlan) {
                            errors.contactEmail = 'This email is already in use for this plan.';
                        }
                    }
                }

                if (isEmail.status === 'fail') {
                    errors.contactEmail = isEmail.errors.email;
                }
            }

            return errors;
        } catch (e) {
            console.error(e);
        }
    };

    const renderNewForm = () => {
        return (
            <div className={style.formRender}>
                <Formik
                    validate={validate}
                    onSubmit={submit}
                    initialValues={props.options.length === 1 && { contactType: props.options[0].value }}
                    render={() => (
                        <Form>
                            <Field
                                name="contactName"
                                component={CccisdInput}
                                label={
                                    <>
                                        Name{' '}
                                        <Tooltip title="Enter the first and last name of the student's contact">
                                            <InfoIcon />
                                        </Tooltip>
                                    </>
                                }
                            />
                            <Field
                                name="contactEmail"
                                component={CccisdInput}
                                label={
                                    <>
                                        Email{' '}
                                        <Tooltip title="Enter the email of the student's contact">
                                            <InfoIcon />
                                        </Tooltip>
                                    </>
                                }
                            />
                            <Field
                                name="contactType"
                                component={CccisdSelect}
                                label="Contact Type"
                                options={props.options}
                                disabled={props.options.length === 1}
                            />

                            <button className="btn btn-primary" type="submit">
                                Submit
                            </button>
                        </Form>
                    )}
                />
            </div>
        );
    };
    const renderEditForm = () => {
        return (
            <div className={style.formRender}>
                <Formik
                    initialValues={{
                        contactName: props.row['fields.contactName'],
                        contactEmail: props.row['fields.contactEmail'],
                        contactType: props.row['fields.contactType'],
                    }}
                    validate={validate}
                    onSubmit={submit}
                    render={() => (
                        <Form>
                            <Field name="contactName" component={CccisdInput} required />
                            <label className={style.required}>
                                <b>Name</b>
                            </label>

                            <Field name="contactEmail" component={CccisdInput} label="Email" />
                            <Field
                                name="contactType"
                                component={CccisdSelect}
                                label="Contact Type"
                                options={props.options}
                                required
                            />

                            <button className="btn btn-primary" type="submit">
                                Submit
                            </button>
                        </Form>
                    )}
                />
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', gap: '0.5em', marginBottom: '0.5em' }}>
            {props.session && (
                <Modal
                    trigger={null}
                    ref={messageRef}
                    render={({ closeModal }) => {
                        const label = props.session.planLabel;
                        return (
                            <MessageForm
                                sessionDeploymentInfo={currentSessionDeploymentInfo}
                                contact={{ fields: { contactEmail: contactInfo.contactEmail } }}
                                row={{ label }}
                                label={label}
                                link={assessmentLink}
                                closeModal={closeModal}
                                messageSuccess={() =>
                                    props.closeStudentContactsModal && props.closeStudentContactsModal()
                                }
                                action="assessment_plan_invite"
                            />
                        );
                    }}
                />
            )}
            {!isEdit && (
                <div className={style.infoBox}>
                    <div style={{ fontSize: '1.8em' }}>
                        <InfoIcon />
                    </div>
                    <div>
                        Select {`'Add New Contact'`} to input the name and email for the {`student’s`} teacher. A
                        contact is required for all assessments that are to be completed by the teacher about the
                        student.
                    </div>
                </div>
            )}
            <Modal
                ref={modal}
                trigger={
                    isEdit ? (
                        <button
                            type="button"
                            style={{ backgroundColor: '#27A746', borderColor: '#27A746' }}
                            className="btn btn-sm btn-success"
                        >
                            <PencilIcon />
                        </button>
                    ) : (
                        <button type="button" className="btn btn-primary">
                            <PlusIcon spaceRight /> Add New Contact
                        </button>
                    )
                }
                title={isEdit ? 'Edit Student Contact' : 'Add Student Contact'}
                beforeClose={() => {
                    props.reloadTable();
                    props.loadData();
                }}
            >
                <div>{isEdit ? renderEditForm() : renderNewForm()}</div>
            </Modal>
        </div>
    );
};

export default FormModal;
